
























































































































import { AxiosResponse } from 'axios';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { fetchAuditorById } from '../../api/auditors';
import { readMmError } from '../../api/mmHttp';
import { formatDate, orDash } from '../../core/format';
import { buildMailtoHref, buildMessengerHref } from '../../core/messengerLinks';
import { MmAuditor } from '../../types/domain';
import { MM_MESSENGER_TITLES, MM_MESSENGERS } from '../../types/enums';

const KEY = 'priceMonitoring.mobileMonitoring.auditors.';

const SECTION_HINT = 'auditors/get';

const STATUS_DEACTIVATED = 'deactivated';

/** Ключ отдельным полем: бэкенд может прислать два одинаковых типа, дубль ключа перепутает узлы */
interface MmAuditorCardMessenger {
	key: string;
	title: string;
	username: string;
	href: string;
}

@Component
export default class AuditorCardModal extends Vue {
	@Prop({ default: false }) readonly visible!: boolean;

	@Prop({ default: null }) readonly auditor!: MmAuditor | null;

	fresh: MmAuditor | null = null;

	isRefreshing: boolean = false;

	isStale: boolean = false;

	staleReason: string = '';

	/** Счётчик поколения отбрасывает поздние ответы: иначе карточка перерисуется чужими данными */
	generation: number = 0;

	@Watch('visible')
	onVisibleChanged(next: boolean): void {
		if (next) {
			this.resetView();
			this.refresh();

			return;
		}

		this.generation += 1;
	}

	mounted(): void {
		// Ветка нужна, если окно смонтируют уже открытым: наблюдатель за visible тогда не сработает
		if (this.visible) {
			this.resetView();
			this.refresh();
		}
	}

	beforeDestroy(): void {
		// Поколение растим и здесь: при уничтожении через v-if переход visible в false не случится
		this.generation += 1;
	}

	get view(): MmAuditor | null {
		return this.fresh !== null ? this.fresh : this.auditor;
	}

	get fullNameText(): string {
		const view = this.view;

		return orDash(view !== null ? view.fullName : null);
	}

	get loginText(): string {
		const view = this.view;

		return orDash(view !== null ? view.login : null);
	}

	get emailText(): string {
		const view = this.view;

		return orDash(view !== null ? view.email : null);
	}

	get emailHref(): string {
		const view = this.view;

		if (view === null || view.email === null || view.email === undefined) {
			return '';
		}

		return buildMailtoHref(view.email);
	}

	get hasEmailLink(): boolean {
		return this.emailHref !== '';
	}

	get phoneText(): string {
		const view = this.view;

		return orDash(view !== null ? view.phone : null);
	}

	get regionText(): string {
		const view = this.view;

		if (view === null) {
			return orDash(null);
		}

		return orDash(view.region === undefined ? null : view.region);
	}

	get createdAtText(): string {
		const view = this.view;

		return formatDate(view !== null ? view.createdAt : null);
	}

	get statusCode(): string {
		const view = this.view;

		if (view === null || view.status === null || view.status === undefined) {
			return '';
		}

		return String(view.status);
	}

	get hasStatus(): boolean {
		return this.statusCode !== '';
	}

	get isDeactivated(): boolean {
		return this.statusCode === STATUS_DEACTIVATED;
	}

	get statusText(): string {
		if (!this.hasStatus) {
			return orDash(null);
		}

		return this.isDeactivated
			? (this.$t(KEY + 'statusDeactivated') as string)
			: (this.$t(KEY + 'statusActive') as string);
	}

	get statusIcon(): string {
		return this.isDeactivated ? 'stop' : 'check-circle';
	}

	get statusClass(): string {
		return this.isDeactivated
			? 'sm-mobilemon__card-status--deactivated'
			: 'sm-mobilemon__card-status--active';
	}

	get messengerItems(): MmAuditorCardMessenger[] {
		const view = this.view;
		const source = view !== null && view.messengers !== null && view.messengers !== undefined
			? view.messengers
			: [];
		const phone = view !== null ? view.phone : null;
		const order = MM_MESSENGERS as string[];
		const result: MmAuditorCardMessenger[] = [];

		for (let i = 0; i < order.length; i += 1) {
			const code = order[i];

			for (let j = 0; j < source.length; j += 1) {
				const item = source[j];

				if (!item || String(item.type).toUpperCase() !== code) {
					continue;
				}

				const username = this.readUsername(item.username);

				result.push({
					key: code + '#' + String(j),
					title: MM_MESSENGER_TITLES[code] ? MM_MESSENGER_TITLES[code] : code,
					username: username,
					href: buildMessengerHref(code, item.username, phone),
				});
			}
		}

		for (let j = 0; j < source.length; j += 1) {
			const item = source[j];

			if (!item) {
				continue;
			}

			const code = String(item.type).toUpperCase();

			if (order.indexOf(code) !== -1) {
				continue;
			}

			const username = this.readUsername(item.username);

			result.push({
				key: 'unknown#' + String(j),
				title: String(item.type),
				username: username,
				href: buildMessengerHref(code, item.username, phone),
			});
		}

		return result;
	}

	get modalTitle(): string {
		return this.$t(KEY + 'cardTitle') as string;
	}

	get fullNameLabel(): string {
		return this.$t(KEY + 'colFullName') as string;
	}

	get loginLabel(): string {
		return this.$t(KEY + 'fieldLogin') as string;
	}

	get statusLabel(): string {
		return this.$t(KEY + 'fieldStatus') as string;
	}

	get emailLabel(): string {
		return this.$t(KEY + 'fieldEmail') as string;
	}

	get phoneLabel(): string {
		return this.$t(KEY + 'fieldPhone') as string;
	}

	get regionLabel(): string {
		return this.$t(KEY + 'fieldRegion') as string;
	}

	get createdAtLabel(): string {
		return this.$t(KEY + 'colCreatedAt') as string;
	}

	get messengersLabel(): string {
		return this.$t(KEY + 'sectionMessengers') as string;
	}

	get messengersEmptyText(): string {
		return this.$t(KEY + 'messengersEmpty') as string;
	}

	get staleText(): string {
		return this.$t(KEY + 'cardStale') as string;
	}

	get refreshingText(): string {
		return this.$t('priceMonitoring.mobileMonitoring.states.loading') as string;
	}

	get closeText(): string {
		return this.$t('shared.actions.close') as string;
	}

	handleCancel(): void {
		this.$emit('cancel');
	}

	private resetView(): void {
		this.fresh = null;
		this.isStale = false;
		this.staleReason = '';
		this.isRefreshing = false;
	}

	private refresh(): void {
		const source = this.auditor;
		const auditorId = source !== null && source !== undefined ? source.id : null;

		// Идентификатор аудитора — строка UUID: приведение к числу дало бы NaN в адресе
		if (auditorId === null || auditorId === undefined || String(auditorId).trim() === '') {
			return;
		}

		this.generation += 1;

		const generation = this.generation;

		this.isRefreshing = true;

		fetchAuditorById(auditorId)
			.then((response: AxiosResponse<MmAuditor>) => {
				if (generation !== this.generation) {
					return;
				}

				this.isRefreshing = false;

				const data = response !== null && response !== undefined ? response.data : null;

				if (data === null || data === undefined) {
					this.isStale = true;
					this.staleReason = '';

					return;
				}

				this.fresh = data;

				this.$emit('refreshed', data);
			})
			.catch((error: any) => {
				if (generation !== this.generation) {
					return;
				}

				this.isRefreshing = false;
				this.isStale = true;

				const mmError = readMmError(error, SECTION_HINT);

				this.staleReason = mmError && mmError.message ? String(mmError.message) : '';
			});
	}

	private readUsername(value: string | null | undefined): string {
		if (value === null || value === undefined) {
			return '';
		}

		return String(value).trim();
	}
}
